import {
    FC,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Button,
    Col,
    Collapse,
    Drawer,
    Dropdown,
    Empty,
    Row,
    Space,
    Spin,
    Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { BsLightbulbFill } from 'react-icons/bs';
import { FiDownload } from 'react-icons/fi';
import { PageHeader } from '../../../PageHeader';
import { useRequestsContext } from '../../context/requests.ctx';
import { IOrderSearch, IRequest } from '../../../../interfaces/requests.interface';
import { getRequestSearch } from '../../../../api/requests.api';
import { IRequestQuery } from '../../../../interfaces/queries.interface';
import { DriverItem } from './DriverItem';
import { OrderSearchFilters } from './OrderSearchFilters';
import { ExportFormat, exportDriverSearch, groupDriversByRound } from './utils/orderSearch.utils';

const { Text } = Typography;
const OrderSearch: FC<{ request: IRequest | null }> = ({ request }) => {
    const { t } = useTranslation('small-fleet-orders');

    const [searchDetails, setSearchDetails] = useState<IOrderSearch[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState(false);

    const [filteredSearchDetails, setFilteredSearchDetails] = useState<IOrderSearch[]>([]);

    const { setViewSearch } = useRequestsContext();

    const fetchOrderSearch = async (id: string) => {
        setLoading(true);

        const params: IRequestQuery = {
            strategy: 'SMALL_FLEETS',
        };

        getRequestSearch<{ total: number; data: IOrderSearch[] }>(id, params)
            .then((response) => {
                setSearchDetails(response.data);
                setTotal(response.total);
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (request?.bookingID) {
            fetchOrderSearch(request.bookingID);
        }
    }, [request?.bookingID]);

    const groupedByRound = useMemo(
        () => groupDriversByRound(filteredSearchDetails),
        [filteredSearchDetails],
    );

    const handleExport = (format: ExportFormat) => {
        if (!searchDetails.length || !request?.bookingNumericId) return;

        const labels = {
            name: t('EXPORT_NAME'),
            email: t('EXPORT_EMAIL'),
            round: t('EXPORT_ROUND'),
            listed: t('EXPORT_LISTED'),
            status: t('EXPORT_STATUS'),
            notified: t('SEARCH_NOTIFIED'),
            notifiedAt: t('EXPORT_NOTIFIED_AT'),
            received: t('SEARCH_RECEIVED'),
            receivedAt: t('EXPORT_RECEIVED_AT'),
            read: t('SEARCH_READ'),
            readAt: t('EXPORT_READ_AT'),
            accepted: t('SEARCH_ACCEPTED'),
            acceptedAt: t('EXPORT_ACCEPTED_AT'),
            battery: t('SEARCH_BATTERY'),
            rejectedReason: t('EXPORT_REJECTED_REASON'),
            yes: t('EXPORT_YES'),
            no: t('EXPORT_NO'),
        };

        exportDriverSearch(searchDetails, format, request.bookingNumericId, labels, t);
    };

    const exportMenuItems = [
        {
            key: 'csv',
            label: t('EXPORT_CSV'),
            onClick: () => handleExport('csv'),
        },
        {
            key: 'xlsx',
            label: t('EXPORT_XLSX'),
            onClick: () => handleExport('xlsx'),
        },
    ];

    const renderRoundHeader = (round: number, driversCount: number) => (
        <Row justify="space-between" align="middle">
            <Text strong style={{ color: 'var(--color-purple)' }}>
                {t('SEARCH_ROUND', { round })}
            </Text>
            <Text type="secondary" style={{ color: 'var(--color-neutral-4)', fontSize: 12 }}>
                {t('SEARCH_DRIVERS_COUNT', { count: driversCount })}
            </Text>
        </Row>
    );

    return (
        <Drawer
            open={Boolean(request)}
            onClose={() => setViewSearch(null)}
            closable={false}
            width={600}
            styles={{ body: { padding: 16 } }}
            style={{ padding: 0 }}
        >
            <PageHeader
                title={`Nº ${request?.bookingNumericId}`}
                style={{ padding: '16px 0' }}
                extra={(
                    <Space size={8}>
                        {searchDetails.length > 0 && (
                            <Dropdown
                                menu={{ items: exportMenuItems }}
                                trigger={['click']}
                            >
                                <Button
                                    type="primary"
                                    icon={<FiDownload style={{ verticalAlign: 'middle' }} />}
                                >
                                    {t('EXPORT_REPORT')}
                                </Button>
                            </Dropdown>
                        )}
                        <Button
                            type="link"
                            onClick={() => setViewSearch(null)}
                        >
                            {t('CLOSE')}
                        </Button>
                    </Space>
                )}
            />
            <Spin spinning={loading}>
                {searchDetails.length > 0 ? (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <div className="help">
                            <div className="help__tag">
                                <Space size={4}>
                                    <BsLightbulbFill />
                                    {t('HELP_TAG')}
                                </Space>
                            </div>
                            <Text>
                                {t('ORDER_SEARCH_HELP')}
                            </Text>
                        </div>

                        <OrderSearchFilters
                            searchDetails={searchDetails}
                            total={total}
                            onFilteredChange={setFilteredSearchDetails}
                        />

                        {filteredSearchDetails.length > 0 ? (
                            <Collapse
                                ghost
                                defaultActiveKey={groupedByRound.map(({ round }) => round.toString())}
                                items={groupedByRound.map(({ round, drivers }) => ({
                                    key: round.toString(),
                                    label: renderRoundHeader(round, drivers.length),
                                    children: drivers.map((driver) => (
                                        <DriverItem key={driver._id} driver={driver} />
                                    )),
                                }))}
                            />
                        ) : (
                            <Empty
                                description={t('NO_RESULTS_FILTERS')}
                            />
                        )}
                    </Space>
                ) : (
                    <Empty
                        className="order-search__empty"
                        description={t('SEARCH_NO_RESULTS')}
                    />
                )}
            </Spin>
        </Drawer>
    );
};

export { OrderSearch };
